import { PrayerTimesResponse, Prayer, PrayerKey, PrayerTimeData } from '../types/prayer';
import { DEFAULT_GOVERNORATE_ID } from '../types/settings';
import dayjs from 'dayjs';

const prayerNames = {
  sobh: {
    en: 'Fajr',
    ar: 'الفجر',
    fr: 'Sobh'
  },
  dhohr: {
    en: 'Dhuhr',
    ar: 'الظهر',
    fr: 'Dhohr'
  },
  aser: {
    en: 'Asr',
    ar: 'العصر',
    fr: 'Aser'
  },
  magreb: {
    en: 'Maghrib',
    ar: 'المغرب',
    fr: 'Maghreb'
  },
  isha: {
    en: 'Isha',
    ar: 'العشاء',
    fr: 'Isha'
  }
};

const tryFetchPrayerTimes = async (date: Date, delegationId: number, gouvernoratId: number = DEFAULT_GOVERNORATE_ID): Promise<PrayerTimesResponse | null> => {
  const formattedDate = dayjs(date).format('YYYY-MM-DD');
  const url = `/api/prayer-times?date=${formattedDate}&gouvernoratId=${gouvernoratId}&delegationId=${delegationId}`;

  try {
    const response = await fetch(url);

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      return null;
    }

    if (!response.ok) {
      return null;
    }

    const text = await response.text();
    return JSON.parse(text) as PrayerTimesResponse;
  } catch (err) {
    console.warn('Failed to fetch prayer times:', err);
    return null;
  }
};

const getPrayerFromData = (data: PrayerTimeData): Prayer[] => {
  return Object.entries(prayerNames)
    .filter(([key]) => key in data)
    .map(([key, names]) => ({
      name: names,
      time: data[key as PrayerKey],
      id: key
    }));
};

export const fetchPrayerTimes = async (date: Date, delegationId: number, gouvernoratId?: number): Promise<Prayer[]> => {
  const response = await tryFetchPrayerTimes(date, delegationId, gouvernoratId);

  if (response?.data) {
    return getPrayerFromData(response.data);
  }

  return [];
};

export const getNextPrayer = (prayers: Prayer[]): Prayer | null => {
  if (!prayers.length) return null;

  const now = dayjs();
  const currentTime = now.format('HH:mm');

  return prayers.find(prayer => prayer.time > currentTime) || prayers[0];
};

export const formatTimeRemaining = (prayerTime: string): string => {
  const [hours, minutes] = prayerTime.split(':');
  const prayer = dayjs().hour(Number(hours)).minute(Number(minutes));
  const now = dayjs();

  if (prayer.isBefore(now)) {
    prayer.add(1, 'day');
  }

  const diff = prayer.diff(now, 'minute');
  const hoursRemaining = Math.floor(diff / 60);
  const minutesRemaining = diff % 60;

  if (hoursRemaining > 0) {
    return `${hoursRemaining}h ${minutesRemaining}m`;
  }
  return `${minutesRemaining}m`;
};
